import { getToken, onMessage } from 'firebase/messaging';
import { messaging, callManageFcmToken } from './firebase';
import { getFirebaseMessagingSwRegistration } from './serviceWorkerManager';

async function resolveTokenDeps(): Promise<{
  swRegistration: ServiceWorkerRegistration;
  vapidKey: string;
} | null> {
  if (!messaging) return null;

  const swRegistration = await getFirebaseMessagingSwRegistration();
  if (!swRegistration) {
    console.error('Firebase Messaging service worker not available');
    return null;
  }

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    console.error('VAPID key is not configured');
    return null;
  }

  return { swRegistration, vapidKey };
}

export async function requestNotificationPermission(): Promise<string | null> {
  if (!messaging) return null;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const deps = await resolveTokenDeps();
    if (!deps) return null;

    const token = await getToken(messaging, {
      vapidKey: deps.vapidKey,
      serviceWorkerRegistration: deps.swRegistration,
    });

    return token;
  } catch (error) {
    console.error('Failed to get FCM token:', error);
    return null;
  }
}

export async function getCurrentFcmToken(): Promise<string | null> {
  if (!messaging) return null;

  try {
    if (Notification.permission !== 'granted') return null;

    const deps = await resolveTokenDeps();
    if (!deps) return null;

    return await getToken(messaging, {
      vapidKey: deps.vapidKey,
      serviceWorkerRegistration: deps.swRegistration,
    });
  } catch (error) {
    console.error('Failed to get current FCM token:', error);
    return null;
  }
}

export function onForegroundMessage(callback: (payload: unknown) => void) {
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}

export type FcmTokenSyncResult = {
  success: boolean;
  action: 'none' | 'registered' | 'error';
  token: string | null;
  message: string;
};

export async function syncFcmToken(): Promise<FcmTokenSyncResult> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return { success: true, action: 'none', token: null, message: 'Notifications not granted' };
  }

  try {
    const currentToken = await getCurrentFcmToken();

    if (!currentToken) {
      return { success: true, action: 'none', token: null, message: 'No token available' };
    }

    await callManageFcmToken({
      token: currentToken,
      action: 'register',
      platform: 'web',
      appKind: 'dashboard',
    });

    return { success: true, action: 'registered', token: currentToken, message: 'Token synced with server' };
  } catch (error) {
    console.error('[FCM Sync] Error syncing token:', error);
    return {
      success: false,
      action: 'error',
      token: null,
      message: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
